/**
 * Single source of truth voor Lead.Status in de offerte-context.
 *
 * Geïmporteerd door zowel de frontend (offerte-detail dropdown, inbox, lijst-
 * kleuren) als de API (server-side write-validatie in api/offertes/set-status.ts).
 * Houd dit bestand PUUR (geen React / browser-globals) zodat het zonder gedoe in
 * een Vercel serverless function bundelt.
 *
 * `Lead.Status` is een niet-restricted SF-picklist met 17 actieve waarden. We
 * schonen de SF-picklist niet op (hangt aan flows/historie + SF-exit nov 2026) —
 * we cureren hier in de app.
 *
 * Betekenissen (praktijk, niet de letterlijke SF-string):
 *  - Offerteaanvraag                    = aangevraagd, nog niet verstuurd
 *  - Niet gereageerd: Open              = verstuurd, geen reactie
 *  - Gereageerd: Komt er snel op terug  = klant denkt na / laat het weten
 *  - Gereageerd: On Hold                = wil het later (na verhuizing etc.)
 *  - Gereageerd: Niet geïnteresseerd    = verloren
 *
 * `Offerte Akkoord` is GEEN dropdown-optie: acceptatie heeft conversie-gevolgen
 * (Products_Complete-gate, conversie via flow-pad) en blijft een aparte actie.
 */

export type OfferteStatusGroup = 'open' | 'reactie' | 'verloren';

export interface OfferteStatusOption {
  /** Letterlijke SF Lead.Status-waarde — wordt zo naar Salesforce geschreven. */
  value: string;
  /** Strak NL-label voor de UI (mag afwijken van de SF-string). */
  label: string;
  /** Tailwind badge-classes. */
  color: string;
  group: OfferteStatusGroup;
}

export const OFFERTE_STATUS_GROUP_LABELS: Record<OfferteStatusGroup, string> = {
  open: 'Open',
  reactie: 'Reactie klant',
  verloren: 'Verloren',
};

/** De gecureerde set die de offerte-dropdown toont. */
export const OFFERTE_STATUSES: readonly OfferteStatusOption[] = [
  { value: 'Offerteaanvraag', label: 'Aangevraagd — nog niet verstuurd', color: 'bg-blue-100 text-blue-700', group: 'open' },
  { value: 'Niet gereageerd: Open', label: 'Verstuurd — geen reactie', color: 'bg-amber-100 text-amber-700', group: 'open' },
  { value: 'Gereageerd: Komt er snel op terug', label: 'Komt erop terug / denkt na', color: 'bg-indigo-100 text-indigo-700', group: 'reactie' },
  { value: 'Gereageerd: On Hold', label: 'On hold — later (bv. na verhuizing)', color: 'bg-violet-100 text-violet-700', group: 'reactie' },
  { value: 'Gereageerd: Niet geïnteresseerd', label: 'Niet geïnteresseerd', color: 'bg-slate-100 text-slate-500', group: 'verloren' },
] as const;

/** Verloren-status (offerte afgewezen). */
export const OFFERTE_CLOSED_STATUS = 'Gereageerd: Niet geïnteresseerd';

/** Open offertes (drijft de /api/offertes/list filter + "te doen"-lijst). */
export const OFFERTE_OPEN_STATUSES = ['Offerteaanvraag', 'Niet gereageerd: Open'] as const;

/**
 * "Wacht op klant" — klant heeft gereageerd, bal ligt bij hen. Drijft het
 * gelijknamige filter op de offertes-lijst zodat deze offertes niet uit beeld
 * vallen zodra ze van de open-lijst af gaan.
 */
export const OFFERTE_WAITING_STATUSES = ['Gereageerd: Komt er snel op terug', 'Gereageerd: On Hold'] as const;

/**
 * Volledige allowlist van geldige SF Lead.Status-waarden — alle 17 actieve
 * picklist-waarden in cinco-prod (geverifieerd 2026-06-08). De write-endpoint
 * valideert hiertegen: geen vrije strings, maar wél breder dan de gecureerde
 * offerte-set zodat de inbox z'n volledige funnel-statussen blijft kunnen zetten.
 */
export const LEAD_STATUS_VALUES: readonly string[] = [
  'New',
  'Contact opgenomen',
  'Open',
  'Gekwalificeerd',
  'Niet geïnteresseerd',
  'Op hold',
  'Offerteaanvraag',
  'Niet gereageerd: Open',
  'Gereageerd: Niet geïnteresseerd',
  'Gereageerd: Komt er snel op terug',
  'Gereageerd: On Hold',
  'Kan niet gereinigd worden',
  'Buiten werkgebied (auto e-mail)',
  'Doorverwezen',
  'Opdracht',
  'Offerte Akkoord',
  'Closed - Lost',
];

const LEAD_STATUS_VALUE_SET = new Set(LEAD_STATUS_VALUES);

export function isValidLeadStatus(value: string): boolean {
  return LEAD_STATUS_VALUE_SET.has(value);
}

/** Badge-kleur voor een status; valt terug op neutraal grijs. */
export function offerteStatusColor(value: string): string {
  return OFFERTE_STATUSES.find((s) => s.value === value)?.color ?? 'bg-slate-100 text-slate-600';
}

/** Strak label voor een status; valt terug op de ruwe SF-waarde. */
export function offerteStatusLabel(value: string): string {
  return OFFERTE_STATUSES.find((s) => s.value === value)?.label ?? value;
}

// ─── Volledige lead-funnel (inbox) ─────────────────────────────────────────
// De inbox bedient álle leads (niet alleen KLEDA-offertes), dus een bredere set
// dan OFFERTE_STATUSES. Eén bron i.p.v. een losse hardcoded lijst in ThreadDetail.
// De 5 offerte-statussen hergebruiken hun OFFERTE_STATUSES-label (geen drift).

export type LeadFunnelGroup = 'open' | 'reactie' | 'verloren' | 'gewonnen';

export const LEAD_FUNNEL_GROUPS: readonly LeadFunnelGroup[] = ['open', 'reactie', 'verloren', 'gewonnen'];

export const LEAD_FUNNEL_GROUP_LABELS: Record<LeadFunnelGroup, string> = {
  open: 'Open',
  reactie: 'Reactie klant',
  verloren: 'Verloren / niet door',
  gewonnen: 'Gewonnen',
};

export interface LeadFunnelStatusOption {
  value: string;
  label: string;
  group: LeadFunnelGroup;
}

const offerteLabel = (value: string): string =>
  OFFERTE_STATUSES.find((s) => s.value === value)?.label ?? value;

export const LEAD_FUNNEL_STATUSES: readonly LeadFunnelStatusOption[] = [
  { value: 'Offerteaanvraag', label: offerteLabel('Offerteaanvraag'), group: 'open' },
  { value: 'Niet gereageerd: Open', label: offerteLabel('Niet gereageerd: Open'), group: 'open' },
  { value: 'Contact opgenomen', label: 'Contact opgenomen', group: 'open' },
  { value: 'Gereageerd: Komt er snel op terug', label: offerteLabel('Gereageerd: Komt er snel op terug'), group: 'reactie' },
  { value: 'Gereageerd: On Hold', label: offerteLabel('Gereageerd: On Hold'), group: 'reactie' },
  { value: 'Op hold', label: 'Op hold (algemeen)', group: 'reactie' },
  { value: 'Gereageerd: Niet geïnteresseerd', label: offerteLabel('Gereageerd: Niet geïnteresseerd'), group: 'verloren' },
  { value: 'Niet geïnteresseerd', label: 'Niet geïnteresseerd (algemeen)', group: 'verloren' },
  { value: 'Kan niet gereinigd worden', label: 'Kan niet gereinigd worden', group: 'verloren' },
  { value: 'Buiten werkgebied (auto e-mail)', label: 'Buiten werkgebied', group: 'verloren' },
  { value: 'Doorverwezen', label: 'Doorverwezen', group: 'verloren' },
  { value: 'Offerte Akkoord', label: 'Offerte akkoord', group: 'gewonnen' },
  { value: 'Opdracht', label: 'Opdracht', group: 'gewonnen' },
];

// ─── Status-badge (weergave) ────────────────────────────────────────────────
// Display-SSOT voor de status-badge op de offertes-lijst, de zoekresultaten en
// de globale ⌘K-zoek. Dekt ALLE 17 actieve SF Lead.Status-waarden — ook de
// legacy/raw statussen die buiten de bewerkbare sets vallen (New, Open,
// Gekwalificeerd, 'Closed - Lost') — zodat zoeken (dat nu élke status vindt)
// altijd een leesbare NL-badge toont i.p.v. een kale Engelse SF-string of een
// grijze onbekende. Dit is puur weergave: de bewerkbare sets blijven bewust
// smaller (OFFERTE_STATUSES = offerte-dropdown, LEAD_FUNNEL_STATUSES = inbox).
//
// Kleur = groep, zodat je in één oogopslag ziet wat openstaat en wat niet:
//   amber = open (actie bij ons) · indigo = reactie klant (bal bij klant)
//   grijs = verloren/dood · groen = gewonnen.

export interface LeadStatusBadge {
  /** Strak NL-label voor de badge. */
  label: string;
  /** Tailwind badge-classes (groeps-kleur, of specifieke offerte-kleur). */
  color: string;
  group: LeadFunnelGroup;
}

const LEAD_FUNNEL_GROUP_BADGE_COLOR: Record<LeadFunnelGroup, string> = {
  open: 'bg-amber-100 text-amber-700',
  reactie: 'bg-indigo-100 text-indigo-700',
  verloren: 'bg-slate-100 text-slate-500',
  gewonnen: 'bg-emerald-100 text-emerald-700',
};

/** Alle 17 SF Lead.Status-waarden → compact NL-label + groep (zie comment). */
const LEAD_STATUS_BADGE: Record<string, { label: string; group: LeadFunnelGroup }> = {
  New: { label: 'Nieuw', group: 'open' },
  Open: { label: 'Open', group: 'open' },
  Gekwalificeerd: { label: 'Gekwalificeerd', group: 'open' },
  'Contact opgenomen': { label: 'Contact opgenomen', group: 'open' },
  Offerteaanvraag: { label: 'Aangevraagd', group: 'open' },
  'Niet gereageerd: Open': { label: 'Verstuurd', group: 'open' },
  'Gereageerd: Komt er snel op terug': { label: 'Komt erop terug', group: 'reactie' },
  'Gereageerd: On Hold': { label: 'On hold', group: 'reactie' },
  'Op hold': { label: 'On hold', group: 'reactie' },
  'Gereageerd: Niet geïnteresseerd': { label: 'Niet geïnteresseerd', group: 'verloren' },
  'Niet geïnteresseerd': { label: 'Niet geïnteresseerd', group: 'verloren' },
  'Kan niet gereinigd worden': { label: 'Kan niet gereinigd', group: 'verloren' },
  'Buiten werkgebied (auto e-mail)': { label: 'Buiten werkgebied', group: 'verloren' },
  Doorverwezen: { label: 'Doorverwezen', group: 'verloren' },
  'Closed - Lost': { label: 'Verloren', group: 'verloren' },
  'Offerte Akkoord': { label: 'Offerte akkoord', group: 'gewonnen' },
  Opdracht: { label: 'Opdracht', group: 'gewonnen' },
};

/**
 * Status-badge voor een Lead.Status-waarde. Werkt voor élke status (val terug
 * op de rauwe waarde + grijs bij een onbekende). Gebruikt de specifieke
 * offerte-dropdown-kleur waar die bestaat, anders de groeps-kleur — zo blijft
 * de lijst-badge in lijn met de offerte-detail-dropdown.
 */
export function leadStatusBadge(value: string): LeadStatusBadge {
  const entry = LEAD_STATUS_BADGE[value];
  if (!entry) {
    return { label: value || '—', color: 'bg-slate-100 text-slate-500', group: 'verloren' };
  }
  const specific = OFFERTE_STATUSES.find((s) => s.value === value)?.color;
  return {
    label: entry.label,
    color: specific ?? LEAD_FUNNEL_GROUP_BADGE_COLOR[entry.group],
    group: entry.group,
  };
}
